import { useEffect, useRef, useState } from "react";

import {
  Card,
  Heading,
  Link,
  MarketingIcon,
  Span
} from "@icg360/design-system";

import type { DataField, DataSection } from "../../../types";
import { formatValueFromBE } from "../../../utils/formatting";
import styles from "./Section.module.css";

interface SectionProps {
  sectionData: DataSection;
}

export const policyTermsToIconNames: Record<string, string> = {
  coverageA: "FullHouse",
  coverageB: "Fence",
  coverageC: "Computer",
  coverageD: "Hotel",
  coverageE: "Shield",
  coverageF: "Stethescope",
  windstorm: "Hurricane",
  hurricane: "Hurricane",
  aop: "StormCloud",
  waterBackup: "Flood",
  identifyFraud: "Theft",
  mechanicalBreakdown: "Tools",
  serviceLines: "FullHouse"
};

const Glossary = ({ text }: { text?: string }) => {
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [lineClampEnabled, setLineClampEnabled] = useState(true);
  const glossaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      glossaryRef.current?.clientHeight &&
      glossaryRef.current?.scrollHeight
    ) {
      setShowSeeMore(
        glossaryRef.current.clientHeight < glossaryRef.current.scrollHeight
      );
    }
  }, []);

  return (
    <>
      <div
        ref={glossaryRef}
        className={`${styles.glossaryText} ${lineClampEnabled ? styles.lineClamp : ""}`}
      >
        {text}
      </div>
      {showSeeMore && (
        <Link onClick={() => setLineClampEnabled(!lineClampEnabled)}>
          {lineClampEnabled ? `Learn more...` : `See less...`}
        </Link>
      )}
    </>
  );
};

export const Section = ({ sectionData }: SectionProps): JSX.Element => {
  const { label } = sectionData;
  const isValueNotIncludedText = (value: DataField["value"]): boolean => {
    if (typeof value === "string") {
      return value.toString().toLowerCase().includes("not included");
    }
    return false;
  };

  return (
    <div className={styles.sectionContainer}>
      <Heading className={styles.sectionHeadingDS} tag="h3" size="lg">
        {label}
      </Heading>
      {sectionData.fields.map((fieldData: DataField) => (
        <Card key={fieldData.term}>
          <div className={styles.iconLabelTerm}>
            <Span color="quiet">
              <MarketingIcon
                name={policyTermsToIconNames[fieldData.term] || "FullHouse"}
              />
            </Span>
            <div className={styles.sectionContent}>
              <div className={styles.labelTerm}>
                <Span bold>{fieldData.label}</Span>
                {isValueNotIncludedText(fieldData.value) ? (
                  <Span
                    color="interactive"
                    className={styles.contactMeTextDS}
                    bold
                  >
                    Contact me
                  </Span>
                ) : (
                  <Span bold>{formatValueFromBE(fieldData.value)}</Span>
                )}
              </div>
              <Glossary text={fieldData.glossaryText} />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export const PaymentSection = ({ sectionData }: SectionProps): JSX.Element => {
  const { label } = sectionData;
  const dueNowIndex = 0;
  return (
    <Card className={styles.paymentCardDS}>
      <div className={styles.numPayments}>
        <Heading size="md" className={styles.numPaymentsHeading}>
          {label}
        </Heading>
        <Span> per policy term</Span>
      </div>
      <div className={styles.paymentTerms}>
        {sectionData.fields.map((fieldData: DataField, index) => (
          <div className={styles.paymentRow} key={fieldData.term}>
            {index === dueNowIndex ? (
              <>
                <Span bold>{fieldData.label}</Span>
                <Heading className={styles.premiumHeadingDS} size="md">
                  {formatValueFromBE(fieldData.value)}
                </Heading>
              </>
            ) : (
              <>
                <Span>{fieldData.label}</Span>
                <Span className={styles.installmentText}>
                  {formatValueFromBE(fieldData.value)}
                </Span>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
